import React from 'react';
import { Reservation } from '../../types';
import { parseSpecialRequests } from '../../services/reservationService';
import { formatDateShort } from '../../utils/dateUtils';

interface DailyReservationRowProps {
  reservation: Reservation;
  panelType: 'arrival' | 'departure';
  /** Show the Check In / Check Out button. Modify shows regardless. */
  showActions?: boolean;
  /** Reservation has a pending manual (cash/check/terminal) payment to collect. */
  hasPaymentDue?: boolean;
  onClick: () => void;
  onCheckIn: () => void;
  onCheckOut: () => void;
  onModify: () => void;
}

const DailyReservationRow: React.FC<DailyReservationRowProps> = ({
  reservation,
  panelType,
  showActions = true,
  hasPaymentDue = false,
  onClick,
  onCheckIn,
  onCheckOut,
  onModify,
}) => {
  const parsed = parseSpecialRequests(reservation.specialRequests);
  // An entry with no site_id is reservation-wide (legacy); an entry with site_id only applies to that site
  const hasCheckedIn = parsed.check_ins?.some(
    ci => ci.site_id == null || ci.site_id === reservation.siteId
  ) ?? false;
  const hasCheckedOut = parsed.check_outs?.some(
    co => co.site_id == null || co.site_id === reservation.siteId
  ) ?? false;

  let statusLabel: string;
  let statusColor: string;
  if (hasCheckedOut) {
    statusLabel = 'Checked Out';
    statusColor = 'var(--status-checked-out)';
  } else if (hasCheckedIn) {
    statusLabel = 'Checked In';
    statusColor = 'var(--status-checked-in)';
  } else {
    statusLabel = 'Confirmed';
    statusColor = 'var(--status-confirmed)';
  }

  const otherGuestNames = reservation.otherGuests
    ? reservation.otherGuests.split(',').map(n => n.trim()).filter(Boolean)
    : [];
  const otherGuestsText = otherGuestNames.length > 0
    ? `+ ${otherGuestNames.join(', ')}`
    : null;

  const occupancyParts = [`${reservation.adults} adult${reservation.adults !== 1 ? 's' : ''}`];
  if (reservation.children > 0) occupancyParts.push(`${reservation.children} child${reservation.children !== 1 ? 'ren' : ''}`);
  if (reservation.pets > 0) occupancyParts.push(`${reservation.pets} pet${reservation.pets !== 1 ? 's' : ''}`);
  if (reservation.vehicles > 0) occupancyParts.push(`${reservation.vehicles} vehicle${reservation.vehicles !== 1 ? 's' : ''}`);

  const showCheckIn = showActions && panelType === 'arrival' && !hasCheckedIn;
  const showCheckOut = showActions && panelType === 'departure' && hasCheckedIn && !hasCheckedOut;

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  const metaText = panelType === 'arrival'
    ? `${reservation.days} night${reservation.days !== 1 ? 's' : ''} · ${occupancyParts.join(', ')}`
    : hasCheckedIn
      ? `Checked in ${formatDateShort(reservation.checkIn)}`
      : `Arriving ${formatDateShort(reservation.checkIn)}`;

  return (
    <div className="daily-row" onClick={onClick}>
      <div className="daily-row__site">{reservation.siteAlias || '—'}</div>
      <div className="daily-row__info">
        <div className="daily-row__name">
          {reservation.guestName}
          {parsed.flagged && (
            <span className="daily-row__flag-badge" title="Flagged - see notes">★</span>
          )}
        </div>
        <div className="daily-row__meta">{metaText}</div>
        {otherGuestsText && (
          <div className="daily-row__guests">{otherGuestsText}</div>
        )}
        {reservation.rvInfo && reservation.rvInfo.length > 0 && (
          <div className="daily-row__rv">
            {reservation.rvInfo.map((rv, i) => {
              const parts = [rv.type];
              if (rv.description) parts.push(rv.description);
              if (rv.length) parts.push(`${rv.length}ft`);
              if (rv.amp) parts.push(`${rv.amp}A`);
              if (rv.licensePlate) parts.push(rv.licensePlate);
              return <span key={i}>{parts.join(' · ')}</span>;
            })}
          </div>
        )}
      </div>
      <div className="daily-row__status">
        {hasPaymentDue && (
          <span
            className="daily-row__status-chip daily-row__payment-due-chip"
            style={{ backgroundColor: 'var(--status-pending, #e08a00)' }}
            title="This reservation has a pending cash/check/terminal payment to collect"
          >
            Payment Due
          </span>
        )}
        <span className="daily-row__status-chip" style={{ backgroundColor: statusColor }}>
          {statusLabel}
        </span>
      </div>
      <div className="daily-row__action">
        {(showCheckIn || showCheckOut) && (
          <button
            className="btn btn-blue btn-sm"
            onClick={(e) => handleActionClick(e, showCheckIn ? onCheckIn : onCheckOut)}
          >
            {showCheckIn ? 'Check In' : 'Check Out'}
          </button>
        )}
        {/* Changes are often called in ahead of arrival, so Modify stays available on
            every row — including upcoming check-ins, which have no check-in button yet */}
        <button
          className="btn btn-secondary btn-sm"
          onClick={(e) => handleActionClick(e, onModify)}
          title="Modify this reservation (dates, sites, occupancy, guest names)"
        >
          Modify
        </button>
      </div>
    </div>
  );
};

export default DailyReservationRow;
